import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export type ScreenSize = 'mobile' | 'tablet' | 'desktop'

const BREAKPOINTS = {
  mobile: 768,
  tablet: 1400
}

const SIDEBAR_WIDTH = {
  expanded: 260,
  collapsed: 64
}

const SIDEBAR_PIN_KEY = 'pj-sidebar-pinned'
function storedSidebarPin(): boolean {
  try { return localStorage.getItem(SIDEBAR_PIN_KEY) === 'true' }
  catch { return false }
}

function saveSidebarPin(pinned: boolean): void {
  try { localStorage.setItem(SIDEBAR_PIN_KEY, String(pinned)) }
  catch { /* storage unavailable */ }
}

export const useUIStore = defineStore('ui', () => {
  // State
  const sidebarManuallyToggled = ref(false) // Track if user manually toggled
  const sidebarCollapsed = ref(false)
  const sidebarPinned = ref(storedSidebarPin())
  const mobileSidebarOpen = ref(false)
  const screenWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024)
  const screenHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 768)
  const screenSize = ref<ScreenSize>('desktop')
  // Where the sidebar's settings-mode back row returns to. Set by the router
  // when the user enters settings from another page in the same workspace;
  // null (→ Home fallback) for deep links or cross-workspace entries.
  const settingsReturnTo = ref<string | null>(null)
  // Profile has its own section menu and keeps its return target independently.
  const profileReturnTo = ref<string | null>(null)
  // Same idea for the knowledge-mode back row — kept as its own value so the
  // two section-takeover modes never clobber each other's back target.
  const knowledgeReturnTo = ref<string | null>(null)

  // Computed
  const isMobile = computed(() => screenSize.value === 'mobile')
  const isTablet = computed(() => screenSize.value === 'tablet')
  const isDesktop = computed(() => screenSize.value === 'desktop')

  // A pin keeps the sidebar open; otherwise tablet widths auto-collapse.
  const isAutoCollapsed = computed(() => {
    if (sidebarPinned.value) return false
    // If user manually toggled, respect their choice
    if (sidebarManuallyToggled.value) {
      return sidebarCollapsed.value
    }
    // Auto behavior: collapsed on tablet, expanded on desktop
    return isTablet.value
  })

  const sidebarWidth = computed(() => {
    if (isMobile.value) return 0
    return isAutoCollapsed.value ? SIDEBAR_WIDTH.collapsed : SIDEBAR_WIDTH.expanded
  })

  // Hover-expanded tablet navigation overlays content. A pinned sidebar
  // stays open and gets its full width so it does not cover the page.
  const contentMarginLeft = computed(() => {
    if (isMobile.value) return 0
    if (isTablet.value && !sidebarPinned.value) return SIDEBAR_WIDTH.collapsed
    return sidebarWidth.value
  })

  const showDividers = computed(() => screenHeight.value > 600)

  // Actions
  function toggleSidebar(): void {
    if (isMobile.value) {
      mobileSidebarOpen.value = !mobileSidebarOpen.value
    } else {
      // Capture the user-visible state BEFORE flipping
      // sidebarManuallyToggled — isAutoCollapsed switches branches based
      // on that flag, so reading it after the flip would return the
      // wrong value on the first tablet click (auto branch said
      // "collapsed", manual branch reads sidebarCollapsed=false, so the
      // toggle would be a no-op and the user has to click twice).
      const currentlyCollapsed = isAutoCollapsed.value
      if (sidebarPinned.value) {
        sidebarPinned.value = false
        saveSidebarPin(false)
      }
      sidebarManuallyToggled.value = true
      sidebarCollapsed.value = !currentlyCollapsed
    }
  }

  function setSidebarCollapsed(collapsed: boolean): void {
    if (collapsed && sidebarPinned.value) {
      sidebarPinned.value = false
      saveSidebarPin(false)
    }
    sidebarManuallyToggled.value = true
    sidebarCollapsed.value = collapsed
  }

  function setSidebarPinned(pinned: boolean): void {
    sidebarPinned.value = pinned
    sidebarManuallyToggled.value = false
    sidebarCollapsed.value = false
    saveSidebarPin(pinned)
  }

  function setMobileSidebarOpen(open: boolean): void {
    mobileSidebarOpen.value = open
  }

  function closeMobileSidebar(): void {
    mobileSidebarOpen.value = false
  }

  function setSettingsReturnTo(path: string | null): void {
    settingsReturnTo.value = path
  }

  function setProfileReturnTo(path: string | null): void {
    profileReturnTo.value = path
  }

  function setKnowledgeReturnTo(path: string | null): void {
    knowledgeReturnTo.value = path
  }

  function updateScreenSize(): void {
    if (typeof window === 'undefined') return

    const prevScreenSize = screenSize.value
    screenWidth.value = window.innerWidth
    screenHeight.value = window.innerHeight

    if (screenWidth.value <= BREAKPOINTS.mobile) {
      screenSize.value = 'mobile'
    } else if (screenWidth.value <= BREAKPOINTS.tablet) {
      screenSize.value = 'tablet'
    } else {
      screenSize.value = 'desktop'
    }

    // Reset manual toggle when screen size category changes
    // This allows auto behavior to take over again
    if (prevScreenSize !== screenSize.value) {
      sidebarManuallyToggled.value = false
      sidebarCollapsed.value = false
    }
  }

  // Initialize screen size and add resize listener
  if (typeof window !== 'undefined') {
    updateScreenSize()
    window.addEventListener('resize', updateScreenSize)
  }

  return {
    // State
    sidebarCollapsed: isAutoCollapsed, // Expose the computed auto-collapsed state
    sidebarPinned,
    mobileSidebarOpen,
    screenWidth,
    screenHeight,
    screenSize,
    settingsReturnTo,
    profileReturnTo,
    knowledgeReturnTo,
    // Computed
    isMobile,
    isTablet,
    isDesktop,
    sidebarWidth,
    contentMarginLeft,
    showDividers,
    // Actions
    toggleSidebar,
    setSidebarCollapsed,
    setSidebarPinned,
    setMobileSidebarOpen,
    closeMobileSidebar,
    setSettingsReturnTo,
    setProfileReturnTo,
    setKnowledgeReturnTo,
    updateScreenSize
  }
})
